// computeTabulationStatus.ts
// Shape-tolerant tabulation computation. Paste this whole file.

/* =========================
   Public types (unchanged)
   ========================= */

export interface TabulationStatus {
  registeredContestantsStatus?: RegisteredContestantStatus[];
  judgesStatus?: JudgeStatus[];
  aocsScoreStatus?: AOCScoreStatus[];
  awardsScoreStatus?: AwardScoreStatus[];
  // Optional rollups
  allContestantsAOCsComplete?: boolean;
  allJudgesScoringComplete?: boolean;
  allAOCsConfigured?: boolean;
  allAwardsConfigured?: boolean;
}

export interface RegisteredContestantStatus {
  registeredContestantId: number;
  contestantName?: string;

  numberOfEnteredAOC?: number;
  numberOfCompletedAOC?: number;
  numberOfIncompletedAOC?: number;
  readyToScoreAwards?: boolean;

  numberOfEnteredAwards?: number;
  numberOfScoredAwards?: number;
  readyToBePlaced?: boolean;
}

export interface JudgeStatus {
  judgeId: number;
  judgeName?: string;

  numberOfActiveContestants?: number;
  numberOfScoredContestants?: number;   // contestants for whom this judge finished all AOC categories they’re entered in
  numberOfAOC?: number;                  // AOCs this judge is expected to score
  numberOfCompletedScoredAOC?: number;   // AOCs where this judge has NO missing scores
  numberOfMissingScores?: number;        // across all AOCs/categories/contestants this judge owes
  scoringComplete?: boolean;
}

export interface AOCScoreStatus {
  aocId: number;
  aocTypeId?: number;
  aocName?: string;

  numberOfExpectedScoresReceived?: number; // expected
  numberOfScoredReceived?: number;         // actual
  hasScoringCriteria?: boolean;            // has categories
  hasContestantCriteria?: boolean;         // has contestant eligibility criteria configured
}

export interface AwardScoreStatus {
  awardId: number;
  awardTypeId?: number;
  awardName?: string;

  numberOfEnteredContestants?: number;
  isUseSupremeScoreCriteria?: boolean;
  isDoubleCrown?: boolean;

  individualAwardScoresStatus?: IndividualAwardScoreStatus[];
}

export interface IndividualAwardScoreStatus {
  awardIndex?: number;

  numberOfMeetCriteriaContestants?: number;
  numberOfFullyEligibleContestants?: number;

  hasWinner?: boolean;
  winners?: IndividualAwardWinner[];
}

export interface IndividualAwardWinner {
  registeredContestantId: number;
  contestantName?: string;
  winningAwardScore?: number;
  isEntered?: boolean;
  meetsCriteria?: boolean;

  // convenience rollups for winner’s overall contest readiness
  isCompetitionComplete?: boolean; // all entered AOCs complete
  isAllAwardsScored?: boolean;     // all entered awards have a score
}

/** Minimal shapes needed from your store (relaxed to avoid refactors) */
export type RegisteredContestantLite = {
  id: number | string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  contestantNumber?: number | string;
  attributes?: any; // may contain ageGroupId, levelId, eventDivisionId, supremeGroupIds, etc.
};

export type JudgeLite = {
  id: number | string;
  name?: string;
  isActive?: boolean;
};

export type AOCScoringCategory = { id: number | string };

export type AOCLite = {
  id: number | string;
  aocTypeId?: number | string;
  name?: string;
  scoringCategories?: AOCScoringCategory[];
  contestantCriteria?: any;
  // optional helpers if you have them:
  enteredContestantIds?: Array<number | string>;
};

export type AwardRelationshipLite = {
  id: number | string;
  awardIndex: number | string;
  registeredPageantContestantId: number | string;
  isEntered?: boolean;
  meetsContestantCriteria?: boolean;
  isFullyEligible?: boolean;
  isWon?: boolean;
  awardScoreBasedOnScoringCriteria?: number | string | null;
};

export type AwardLite = {
  id: number | string;
  awardTypeId?: number | string;
  name?: string;
  numberAvailable?: number | string;
  matchingAwardRelationships?: { awardRelationship: AwardRelationshipLite }[];
  specificAwardFullCriteria?: {
    additionalScoringDetails?: {
      isDoubleCrown?: boolean;
      isUseSupremeScoreCriteria?: boolean;
    };
    contestantCriteria?: any[];
  };
};

export type AOCScoreRecord = {
  aocId: number | string;
  judgeId: number | string;
  contestantId: number | string;
  scoringCategoryId: number | string;
  score?: number | null; // counts as "received" if not null/undefined
};

/* =========================
   Utils
   ========================= */

const toNum = (v: any): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const toId = (v: any): number => Number(v);

/* =========================
   Guards + Normalizers
   ========================= */

const isJudgeLite = (x: any): x is JudgeLite =>
  x && (typeof x.id === 'string' || typeof x.id === 'number');

const isJudgeLiteArray = (arr: any): arr is JudgeLite[] =>
  Array.isArray(arr) && arr.every(isJudgeLite);

const isAwardLite = (a: any): a is AwardLite =>
  a && (typeof a.id === 'string' || typeof a.id === 'number');

const isAwardLiteArray = (arr: any): arr is AwardLite[] =>
  Array.isArray(arr) && arr.every(isAwardLite);

const isAOCLiteArray = (arr: any): arr is AOCLite[] =>
  Array.isArray(arr) && arr.every(a => a && (typeof a.id === 'string' || typeof a.id === 'number'));

// personnel[] -> JudgeLite[] (numeric fallback id to play nice with toId())
function normalizeJudgesFromPersonnel(personnel: any[] = []): JudgeLite[] {
  return (personnel ?? [])
    .filter(p => p.personnelTypeId === 3 || p.isJudge === true || p.role === 'Judge')
    .map((p: any, idx: number) => {
      const id =
        p.personnelUserId ??
        p.id ??
        (idx + 1); // numeric fallback (avoids NaN when toId() is applied)
      const name =
        (p.userName ??
          [p.firstName, p.lastName].filter(Boolean).join(' ') ??
          p.emailAddress ??
          '').trim() || `Judge ${String.fromCharCode(65 + idx)}`;
      return { id, name, isActive: p.isActive ?? true };
    });
}

// pageantAvailableAwards[] -> AwardLite[]
function normalizeAwardsFromPageant(awards: any[] = []): AwardLite[] {
  return (awards ?? []).map((aw: any) => {
    const rels = (aw.matchingAwardRelationships ?? []).map((mar: any, idx: number) => {
      const r = mar?.awardRelationship ?? mar;

      const rid =
        r?.id ??
        `${aw.id}:${r?.registeredPageantContestantId ?? 'rc'}:${r?.awardIndex ?? idx}`;

      const rel: AwardRelationshipLite = {
        id: rid,
        awardIndex: r?.awardIndex ?? 0,
        registeredPageantContestantId:
          r?.registeredPageantContestantId ?? r?.registeredContestantId ?? 0,
        isEntered: !!r?.isEntered,
        meetsContestantCriteria: !!r?.meetsContestantCriteria,
        isFullyEligible: !!r?.isFullyEligible,
        isWon: !!r?.isWon,
        awardScoreBasedOnScoringCriteria:
          r?.awardScoreBasedOnScoringCriteria ?? r?.score ?? null,
      };

      return { awardRelationship: rel };
    });

    const isUseSupreme =
      !!aw?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreCriteria ||
      !!aw?.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreSettings ||
      !!(aw as any)?.isUseSupremeScoreCriteria;

    const isDoubleCrown =
      !!aw?.specificAwardFullCriteria?.additionalScoringDetails?.isDoubleCrown ||
      !!(aw as any)?.isDoubleCrown;

    return {
      id: aw.id ?? 0,
      name: aw.name ?? '',
      awardTypeId: aw.awardTypeId ?? 0,
      numberAvailable: aw.numberAvailable ?? 1,
      matchingAwardRelationships: rels,
      specificAwardFullCriteria: {
        additionalScoringDetails: {
          isUseSupremeScoreCriteria: isUseSupreme,
          isDoubleCrown,
        },
        contestantCriteria: aw?.specificAwardFullCriteria?.contestantCriteria ?? [],
      },
    } as AwardLite;
  });
}

// pageantAvailableAOC[] -> AOCLite[]  (pass-through if already compatible)
function normalizeAOCs(aocs: any[] = []): AOCLite[] {
  return aocs as unknown as AOCLite[];
}

// contestants -> RegisteredContestantLite[]
function normalizeContestants(arr: any[] = []): RegisteredContestantLite[] {
  return (arr ?? []).map((c: any) => ({
    id: c.id ?? c.registeredContestantId ?? c.contestantId ?? c.personId ?? 0,
    fullName: c.fullName ?? [c.firstName, c.lastName].filter(Boolean).join(' '),
    firstName: c.firstName,
    lastName: c.lastName,
    contestantNumber: c.contestantNumber ?? c.number,
    attributes: c.attributes ?? {},
  }));
}

// scores: accept AOCScoreRecord[] OR { [contestantId]: ReceivedScoreBaseModel[] }
function normalizeScores(input: {
  aocScores?: any[];
  allScoresByContestant?: Record<string | number, any[]> | undefined;
}): AOCScoreRecord[] {
  if (
    Array.isArray(input.aocScores) &&
    input.aocScores.every(
      (s) =>
        s &&
        'aocId' in s &&
        'judgeId' in s &&
        'contestantId' in s &&
        'scoringCategoryId' in s
    )
  ) {
    return input.aocScores as AOCScoreRecord[];
  }

  const byContestant = input.allScoresByContestant ?? {};
  const out: AOCScoreRecord[] = [];
  for (const [cid, rows] of Object.entries(byContestant)) {
    for (const r of rows ?? []) {
      out.push({
        aocId: r.aocId ?? r.pageantAvailableAOCId ?? r.categoryGroupId,
        judgeId: r.judgeId ?? r.personnelUserId ?? r.scoredByUserId,
        contestantId: cid,
        scoringCategoryId: r.scoringCategoryId ?? r.categoryId ?? r.criteriaId,
        score: r.score ?? r.value ?? r.points ?? null,
      });
    }
  }
  return out;
}

/* =========================
   Store-derived helpers
   ========================= */

// Reads the contestant’s AOC detail row if your store provides it.
const getContestantAOCDetail = (c: any, aocId: number) =>
  (c?.contestantAreasOfCompetitionDetails ?? []).find(
    (d: any) => Number(d?.aocId) === aocId
  );

/* =========================
   Main compute
   ========================= */

/**
 * Compute TabulationStatus from your data.
 * You can now pass your raw shapes directly, e.g.:
 *
 * computeTabulationStatus({
 *   registeredContestants: registeredContestantsNew,
 *   judges: personnel,
 *   aocs: pageantAvailableAOC,
 *   awards: pageantAvailableAwards,
 *   allScoresByContestant: allFetchedScores,
 * })
 */
export function computeTabulationStatus(input: any): TabulationStatus {
  // Normalize all incoming shapes
  const contestants: RegisteredContestantLite[] = normalizeContestants(
    input.contestants ?? input.registeredContestants ?? input.registeredContestantsNew ?? []
  );

  const judges: JudgeLite[] =
    isJudgeLiteArray(input.judges) ? input.judges : normalizeJudgesFromPersonnel(input.judges ?? []);

  const aocs: AOCLite[] =
    isAOCLiteArray(input.aocs) ? input.aocs : normalizeAOCs(input.aocs ?? input.pageantAvailableAOC ?? []);

  const awards: AwardLite[] =
    isAwardLiteArray(input.awards) ? input.awards : normalizeAwardsFromPageant(input.awards ?? input.pageantAvailableAwards ?? []);

  const aocScores: AOCScoreRecord[] = normalizeScores({
    aocScores: input.aocScores,
    allScoresByContestant: input.allScoresByContestant ?? input.allFetchedScores,
  });

  const isContestantActive: (c: RegisteredContestantLite) => boolean =
    input.isContestantActive ?? (() => true);
  const isContestantEnteredInAOC:
    | ((contestantId: number, aoc: AOCLite) => boolean)
    | undefined = input.isContestantEnteredInAOC;
  const isJudgeActive: (j: JudgeLite) => boolean =
    input.isJudgeActive ?? ((j: JudgeLite) => j.isActive !== false);
  const formatContestantName:
    | ((c: RegisteredContestantLite) => string)
    | undefined = input.formatContestantName;

  // ---- indices ----
  const contestantsById = new Map<number, RegisteredContestantLite>();
  contestants.forEach((c) => contestantsById.set(toId(c.id), c));

  const judgesActive = judges.filter(isJudgeActive);
  const judgeIdsActive = judgesActive.map((j) => toId(j.id));
  const judgeIdSet = new Set(judgeIdsActive);

  const aocById = new Map<number, AOCLite>();
  aocs.forEach((a) => aocById.set(toId(a.id), a));

  const categoriesByAOC = new Map<number, number>(); // aocId -> categoryCount
  aocs.forEach((a) => categoriesByAOC.set(toId(a.id), (a.scoringCategories ?? []).length));

  // ---- AOC entered map ----
  const enteredByAOC = new Map<number, Set<number>>();
  for (const a of aocs) {
    const aid = toId(a.id);
    const set = new Set<number>();

    if (isContestantEnteredInAOC) {
      // explicit override
      contestants.forEach((c) => {
        if (isContestantEnteredInAOC(toId(c.id), a)) set.add(toId(c.id));
      });
    } else if (Array.isArray(a.enteredContestantIds)) {
      // AOCLite carries the list
      a.enteredContestantIds.forEach((cid) => set.add(toId(cid)));
    } else if (contestants.some((c: any) => Array.isArray(c?.contestantAreasOfCompetitionDetails))) {
      // Use per-contestant details (preferred if present)
      for (const c of contestants as any[]) {
        const cid = toId(c.id);
        const det = getContestantAOCDetail(c, aid);
        if (det?.isEntered) set.add(cid);
      }
    } else {
      // Fallback: infer from scores (any score row => “entered”)
      aocScores.forEach((s) => {
        if (toId(s.aocId) === aid) set.add(toId(s.contestantId));
      });
    }

    // Filter to active contestants
    for (const cid of Array.from(set)) {
      const c = contestantsById.get(cid);
      if (!c || !isContestantActive(c)) set.delete(cid);
    }
    enteredByAOC.set(aid, set);
  }

  // ---- score indexes ----
  const scoredByAOCJudgeContestant = new Map<
    number,
    Map<number, Map<number, Set<number>>>
  >();

  const receivedByAOC = new Map<number, number>(); // total scored (non-null) rows for this AOC (judge*contestant*category)
  for (const s of aocScores) {
    const aocId = toId(s.aocId);
    const judgeId = toId(s.judgeId);
    const contestantId = toId(s.contestantId);
    const catId = toId(s.scoringCategoryId);

    if (!judgeIdSet.has(judgeId)) continue; // only count active judges
    if (s.score == null) continue; // only count received

    receivedByAOC.set(aocId, (receivedByAOC.get(aocId) ?? 0) + 1);

    if (!scoredByAOCJudgeContestant.has(aocId)) scoredByAOCJudgeContestant.set(aocId, new Map());
    const byJudge = scoredByAOCJudgeContestant.get(aocId)!;
    if (!byJudge.has(judgeId)) byJudge.set(judgeId, new Map());
    const byContestant = byJudge.get(judgeId)!;
    if (!byContestant.has(contestantId)) byContestant.set(contestantId, new Set());
    byContestant.get(contestantId)!.add(catId);
  }

  // ---------- AOCScoreStatus ----------
  const aocsScoreStatus: AOCScoreStatus[] = aocs.map((a) => {
    const aid = toId(a.id);
    const catCount = categoriesByAOC.get(aid) ?? 0;
    const enteredCount = (enteredByAOC.get(aid)?.size ?? 0);
    const judgeCount = judgeIdsActive.length;

    const expected = enteredCount * judgeCount * catCount;
    const actual = receivedByAOC.get(aid) ?? 0;

    return {
      aocId: aid,
      aocTypeId: toNum(a.aocTypeId),
      aocName: a.name ?? `AOC ${aid}`,
      numberOfExpectedScoresReceived: expected,
      numberOfScoredReceived: actual,
      hasScoringCriteria: catCount > 0,
      hasContestantCriteria:
        !!(a as any)?.contestantCriteria && Array.isArray((a as any)?.contestantCriteria)
          ? (a as any).contestantCriteria.length > 0
          : !!(a as any)?.contestantCriteria,
    };
  });

  // Prefer the store’s "aocComplete" flag; otherwise fall back to score coverage.
  const isAOCCompleteForContestant = (aocId: number, contestantId: number) => {
    const c = contestantsById.get(contestantId);
    const det = c ? getContestantAOCDetail(c as any, aocId) : undefined;

    if (typeof det?.aocComplete === 'boolean') {
      return !!det.aocComplete;
    }

    // Fallback to score-based completion
    const catCount = categoriesByAOC.get(aocId) ?? 0;
    if (catCount === 0) return false;
    for (const judgeId of judgeIdsActive) {
      const byJudge = scoredByAOCJudgeContestant.get(aocId)?.get(judgeId);
      const set = byJudge?.get(contestantId);
      if (!set || set.size < catCount) return false;
    }
    return true;
  };

  // ---------- JudgeStatus ----------
  const judgesStatus: JudgeStatus[] = judgesActive.map((j) => {
    const jid = toId(j.id);

    let expectedForJudge = 0;
    let receivedForJudge = 0;
    let numberOfCompletedScoredAOC = 0;
    let numberOfScoredContestants = 0;

    const allEnteredContestantIds = new Set<number>();

    for (const a of aocs) {
      const aid = toId(a.id);
      const catCount = categoriesByAOC.get(aid) ?? 0;
      const entered = Array.from(enteredByAOC.get(aid) ?? new Set<number>());
      if (catCount === 0) continue;

      expectedForJudge += entered.length * catCount;

      const byContestant = scoredByAOCJudgeContestant.get(aid)?.get(jid) ?? new Map<number, Set<number>>();
      let completeThisAOC = true;

      for (const cid of entered) {
        allEnteredContestantIds.add(cid);
        const set = byContestant.get(cid);
        const isCompleteForContestant = set && set.size >= catCount;
        if (isCompleteForContestant) {
          receivedForJudge += catCount;
        } else {
          receivedForJudge += set ? set.size : 0;
          completeThisAOC = false;
        }
      }

      if (entered.length > 0 && completeThisAOC) {
        numberOfCompletedScoredAOC += 1;
      }
    }

    for (const cid of allEnteredContestantIds) {
      let allComplete = true;
      for (const a of aocs) {
        const aid = toId(a.id);
        const catCount = categoriesByAOC.get(aid) ?? 0;
        if (catCount === 0) continue;
        const entered = enteredByAOC.get(aid)?.has(cid) ?? false;
        if (!entered) continue;
        const set = scoredByAOCJudgeContestant.get(aid)?.get(jid)?.get(cid);
        if (!set || set.size < catCount) {
          allComplete = false;
          break;
        }
      }
      if (allComplete) numberOfScoredContestants += 1;
    }

    const numberOfActiveContestants = allEnteredContestantIds.size;
    const numberOfMissingScores = Math.max(0, expectedForJudge - receivedForJudge);
    const scoringComplete = numberOfMissingScores === 0;

    return {
      judgeId: jid,
      judgeName: j.name ?? `Judge ${jid}`,
      numberOfActiveContestants,
      numberOfScoredContestants,
      numberOfAOC: aocs.length,
      numberOfCompletedScoredAOC,
      numberOfMissingScores,
      scoringComplete,
    };
  });

  // ---------- RegisteredContestantStatus ----------
  const registeredContestantsStatus: RegisteredContestantStatus[] = contestants.map((c) => {
    const cid = toId(c.id);

    const enteredAOCs = aocs.filter((a) => (enteredByAOC.get(toId(a.id))?.has(cid) ?? false));
    const numberOfEnteredAOC = enteredAOCs.length;

    const numberOfCompletedAOC = enteredAOCs.reduce((acc, a) => {
      const aid = toId(a.id);
      return acc + (isAOCCompleteForContestant(aid, cid) ? 1 : 0);
    }, 0);

    const numberOfIncompletedAOC = Math.max(0, numberOfEnteredAOC - numberOfCompletedAOC);

    // Prefer store rollup if provided
    const storeReady = (c as any)?.competitionStatus?.isReadyToScore;
    const readyToScoreAwards =
      typeof storeReady === 'boolean'
        ? !!storeReady
        : numberOfEnteredAOC > 0 && numberOfIncompletedAOC === 0;

    let numberOfEnteredAwards = 0;
    let numberOfScoredAwards = 0;

    for (const aw of awards) {
      const rels = aw.matchingAwardRelationships ?? [];
      for (const row of rels) {
        const rel = row?.awardRelationship;
        if (!rel) continue;
        if (toId(rel.registeredPageantContestantId) !== cid) continue;
        if (rel.isEntered) numberOfEnteredAwards += 1;
        const scoreVal = rel.awardScoreBasedOnScoringCriteria;
        if (rel.isEntered && scoreVal != null && Number.isFinite(Number(scoreVal))) {
          numberOfScoredAwards += 1;
        }
      }
    }

    const readyToBePlaced =
      numberOfEnteredAwards > 0 && numberOfScoredAwards === numberOfEnteredAwards;

    const name =
      (formatContestantName?.(c) ??
        c.fullName ??
        `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim()) || `ID ${cid}`;

    return {
      registeredContestantId: cid,
      contestantName: name,
      numberOfEnteredAOC,
      numberOfCompletedAOC,
      numberOfIncompletedAOC,
      readyToScoreAwards,
      numberOfEnteredAwards,
      numberOfScoredAwards,
      readyToBePlaced,
    };
  });

  const rcStatusById = new Map<number, RegisteredContestantStatus>();
  registeredContestantsStatus.forEach((s) => rcStatusById.set(s.registeredContestantId, s));

  // ---------- AwardScoreStatus ----------
  const awardsScoreStatus: AwardScoreStatus[] = awards.map((aw) => {
    const awardId = toId(aw.id);
    const awardTypeId = toNum(aw.awardTypeId);
    const rels = aw.matchingAwardRelationships ?? [];

    const numberOfEnteredContestants = rels.reduce((acc, row) => {
      const rel = row?.awardRelationship;
      return rel?.isEntered ? acc + 1 : acc;
    }, 0);

    const isDoubleCrown = !!aw.specificAwardFullCriteria?.additionalScoringDetails?.isDoubleCrown;
    const isUseSupremeScoreCriteria = !!aw.specificAwardFullCriteria?.additionalScoringDetails?.isUseSupremeScoreCriteria;

    // group by awardIndex
    const byIndex = new Map<number, AwardRelationshipLite[]>();
    for (const row of rels) {
      const rel = row?.awardRelationship;
      if (!rel) continue;
      const idx = toId(rel.awardIndex);
      if (!byIndex.has(idx)) byIndex.set(idx, []);
      byIndex.get(idx)!.push(rel);
    }

    const individualAwardScoresStatus: IndividualAwardScoreStatus[] = [];
    const indices = Array.from(byIndex.keys()).sort((a, b) => a - b);
    if (indices.length === 0) indices.push(1); // ensure at least one index row

    for (const idx of indices) {
      const relsAtIndex = byIndex.get(idx) ?? [];
      const numberOfMeetCriteriaContestants = relsAtIndex.filter((r) => r.meetsContestantCriteria).length;
      const numberOfFullyEligibleContestants = relsAtIndex.filter((r) => r.isFullyEligible).length;

      const winners = relsAtIndex
        .filter((r) => r.isWon)
        .map<IndividualAwardWinner>((r) => {
          const rcid = toId(r.registeredPageantContestantId);
          const rc = contestantsById.get(rcid);
          const rcStatus = rcStatusById.get(rcid);

          const name =
            (rc?.fullName ??
              `${rc?.firstName ?? ''} ${rc?.lastName ?? ''}`.trim()) || `ID ${rcid}`;

          return {
            registeredContestantId: rcid,
            contestantName: name,
            winningAwardScore:
              r.awardScoreBasedOnScoringCriteria != null
                ? Number(r.awardScoreBasedOnScoringCriteria)
                : undefined,
            isEntered: !!r.isEntered,
            meetsCriteria: !!r.meetsContestantCriteria,
            isCompetitionComplete: !!rcStatus?.readyToScoreAwards,
            isAllAwardsScored:
              !!rcStatus && rcStatus.numberOfEnteredAwards === rcStatus.numberOfScoredAwards,
          };
        });

      individualAwardScoresStatus.push({
        awardIndex: idx,
        numberOfMeetCriteriaContestants,
        numberOfFullyEligibleContestants,
        hasWinner: winners.length > 0,
        winners,
      });
    }

    return {
      awardId,
      awardTypeId,
      awardName: aw.name ?? `Award ${awardId}`,
      numberOfEnteredContestants,
      isUseSupremeScoreCriteria,
      isDoubleCrown,
      individualAwardScoresStatus,
    };
  });

  // ---------- Top-level rollups ----------
  const allContestantsAOCsComplete =
    (registeredContestantsStatus ?? []).every((s) => (s.numberOfIncompletedAOC ?? 0) === 0);

  const allJudgesScoringComplete = (judgesStatus ?? []).every((j) => j.scoringComplete);

  const allAOCsConfigured = (aocsScoreStatus ?? []).every(
    (a) => a.hasScoringCriteria && a.hasContestantCriteria
  );

  const allAwardsConfigured = (awards ?? []).every((aw) => {
    const hasAnyCriteria =
      (aw.specificAwardFullCriteria?.contestantCriteria?.length ?? 0) > 0 ||
      !!aw.specificAwardFullCriteria?.additionalScoringDetails;
    return hasAnyCriteria;
  });

  return {
    registeredContestantsStatus,
    judgesStatus,
    aocsScoreStatus,
    awardsScoreStatus,
    allContestantsAOCsComplete,
    allJudgesScoringComplete,
    allAOCsConfigured,
    allAwardsConfigured,
  };
}
